import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/marketplace/SEOHead';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth';
import {
  SearchCheck, ShieldCheck, Lock, FileBadge, Sparkles,
  MessagesSquare, Gift, PenSquare, ArrowRight, Factory, Scale,
} from 'lucide-react';

// ---------- Outcome-led feature blocks (only what's actually shipped) ----------
const FEATURES = [
  {
    icon: SearchCheck,
    title: 'Find the right industrial gear or service in seconds',
    body: 'Search Sell, Wanted, Rent, and Service listings across machinery, spares, raw materials, tools, material-handling equipment — and industrial services like AMC, fabrication, calibration, installation, engineering, and consultancy. Filter by location, category, and price — see only what matches your factory floor.',
    testId: 'feature-search',
  },
  {
    icon: ShieldCheck,
    title: 'Every listing is human-moderated',
    body: 'A moderator reviews every ad before it goes live. Spam, personal items, and scams get filtered out — so what you see is real B2B supply and demand, not clutter.',
    testId: 'feature-moderation',
  },
  {
    icon: Lock,
    title: 'Seller contacts are gated for trust',
    body: 'Buyers see full listing details up front, but seller phone/email is revealed only after a real inquiry — so genuine sellers stay reachable and cold-callers stay away.',
    testId: 'feature-gated-contact',
  },
  {
    icon: FileBadge,
    title: 'GST-verified sellers stand out',
    body: 'Submit your GSTIN once, get a green verified badge everywhere your listings appear. Buyers filter for verified sellers when a purchase is serious — verification directly moves your inquiries.',
    testId: 'feature-verification',
  },
  {
    icon: Sparkles,
    title: 'Auto-watermarked photos + AI-assisted SEO',
    body: 'Every uploaded photo gets an IndusFact watermark so your images can\'t be stolen. Ad titles, descriptions, and meta tags are optimised automatically — your listing shows up on Google without any SEO work.',
    testId: 'feature-watermark-seo',
  },
  {
    icon: MessagesSquare,
    title: 'Inquiries land in one clean inbox',
    body: 'Every buyer message about your listings comes to a single dashboard — respond, filter by listing, and track reveal logs. No juggling WhatsApp, email, and phone notes.',
    testId: 'feature-inquiries',
  },
  {
    icon: Gift,
    title: 'Earn points on every purchase and referral',
    body: 'Cash-back points on paid plans, referral rewards when friends sign up and buy, and weekly login streaks. Redeem points on future purchases — your first plan pays part of your second.',
    testId: 'feature-rewards',
  },
  {
    icon: Scale,
    title: 'Compare up to four listings side by side',
    body: 'Shortlist comparable machines, rentals or services and read their specifications in one table — condition, year, running hours, rates, response times — then send a single quote request to every seller at once instead of mailing them one by one. Free to use; saving and sharing a named comparison comes with a paid plan.',
    testId: 'feature-compare',
  },
  {
    icon: PenSquare,
    title: 'Learn from the industry blog',
    body: 'Category-wise how-to guides — buying a used CNC, preventive maintenance for VMCs, hydraulic press safety — written for shop-floor engineers, not marketers. Free to read.',
    testId: 'feature-blog',
  },
];

const Features = () => {
  const { user } = useAuth();
  const primaryCta = user ? { to: '/post', label: 'Post your first listing' } : { to: '/register', label: 'Create your free account' };

  return (
    <>
      <SEOHead
        title='Indus Fact Features: Verified Sellers & GST-Checked Listings'
        description='Explore Indus Fact features: GST-verified sellers, moderated listings, gated contact reveal, India&apos;s trusted B2B industrial marketplace.'
        keywords='IndusFact features, how does IndusFact work, industrial marketplace India, verified sellers, GST verified, machinery listings'
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/features` : undefined}
      />

      {/* Hero */}
      <section className='relative indus-hero-bg indus-noise overflow-hidden'>
        {/* Dark tint overlay so white text is legible over the pale gradient. */}
        <div className='absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/70 to-orange-900/60 pointer-events-none' aria-hidden />
        <div className='container mx-auto px-4 py-14 md:py-20 max-w-6xl relative'>
          <div className='max-w-3xl'>
            <Badge variant='outline' className='mb-4 border-white/40 text-white bg-white/15 backdrop-blur' data-testid='features-hero-badge'>
              <Factory className='h-3 w-3 mr-1.5' /> Purpose-built for Indian B2B
            </Badge>
            <h1 className='text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight' data-testid='features-hero-title'>
              Everything you need to buy, sell, rent and hire industrial services — in one place.
            </h1>
            <p className='mt-5 text-lg text-white/90 max-w-2xl'>
              IndusFact is the classifieds platform for the people who run India&apos;s factories. No home appliances. No car ads. Just machinery, spares, materials, and the tools that keep production moving.
            </p>
            <div className='mt-8 flex flex-wrap gap-3'>
              <Link to={primaryCta.to}>
                <Button size='lg' className='indus-cta shadow-lg' data-testid='features-primary-cta'>
                  {primaryCta.label} <ArrowRight className='h-4 w-4 ml-2' />
                </Button>
              </Link>
              <Link to='/sell'>
                <Button size='lg' variant='outline' className='bg-white/15 text-white border-white/50 hover:bg-white/25 hover:text-white' data-testid='features-secondary-cta'>
                  Browse listings
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className='container mx-auto px-4 py-14 md:py-20 max-w-6xl'>
        <div className='mb-10 max-w-2xl'>
          <div className='text-xs uppercase tracking-widest text-orange-600 font-semibold mb-2'>What you get on IndusFact</div>
          <h2 className='text-3xl font-bold'>Not a broker. Not a lead-gen scam. A real marketplace.</h2>
          <p className='text-muted-foreground mt-3'>Every feature below is live today. Nothing hypothetical, nothing under-construction.</p>
        </div>

        <div className='grid md:grid-cols-2 gap-5' data-testid='features-grid'>
          {FEATURES.map(({ icon: Icon, title, body, testId }) => (
            <Card key={testId} className='p-6 hover:shadow-md transition-shadow' data-testid={testId}>
              <div className='flex items-start gap-4'>
                <div className='shrink-0 h-11 w-11 rounded-lg bg-orange-50 border border-orange-100 flex items-center justify-center text-orange-600'>
                  <Icon className='h-5 w-5' />
                </div>
                <div>
                  <div className='font-semibold text-base leading-snug'>{title}</div>
                  <p className='text-sm text-muted-foreground mt-2 leading-relaxed'>{body}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* "Always evolving" reassurance strip — deliberately vague so we don't
          leak the product roadmap. Purpose is to signal ongoing momentum. */}
      <section className='border-y bg-muted/30'>
        <div className='container mx-auto px-4 py-12 max-w-6xl'>
          <div className='max-w-2xl mx-auto text-center' data-testid='features-always-evolving'>
            <div className='inline-flex items-center gap-2 rounded-full border bg-background px-3 py-1 text-[11px] uppercase tracking-widest text-slate-600 font-semibold mb-4'>
              <Sparkles className='h-3.5 w-3.5 text-amber-600' /> Continuously improving
            </div>
            <h2 className='text-2xl md:text-3xl font-bold leading-tight'>
              And there&apos;s more on the way.
            </h2>
            <p className='text-sm md:text-base text-muted-foreground mt-3 leading-relaxed'>
              IndusFact is built with the factory floor in mind — and we keep sharpening the platform based on how real buyers, sellers, and service providers use it every day. New capabilities land regularly, quietly, and always in service of making industrial B2B faster and more trustworthy.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className='container mx-auto px-4 py-14 md:py-16 max-w-6xl'>
        <Card className='p-8 md:p-12 bg-gradient-to-br from-orange-50 via-white to-orange-50 border-orange-100 text-center'>
          <h3 className='text-2xl md:text-3xl font-bold'>Ready to move your first machine?</h3>
          <p className='text-muted-foreground mt-3 max-w-xl mx-auto'>
            Free to list. Free to inquire. Moderated in hours, not days.
          </p>
          <div className='mt-6 flex flex-wrap gap-3 justify-center'>
            <Link to={primaryCta.to}>
              <Button size='lg' className='indus-cta' data-testid='features-bottom-cta'>
                {primaryCta.label} <ArrowRight className='h-4 w-4 ml-2' />
              </Button>
            </Link>
            <Link to='/pricing'>
              <Button size='lg' variant='outline' data-testid='features-see-pricing'>
                See pricing
              </Button>
            </Link>
          </div>
        </Card>
      </section>
    </>
  );
};

export default Features;
